import camelCase from 'lodash/camelCase';
import isNil from 'lodash/isNil';
import mergeWith from 'lodash/mergeWith';
import { ELEMENT_MODAL_LOGO, ELEMENT_FOOTER_CTA } from 'common/constants/virtualEvent';

import {
    allStylesMap,
    logoMap,
    metaMap,
    stylesMapKeys,
    metaMapKeys,
    checkForUnknownKeys,
    EMPTY_BORDER_WIDTH,
} from '../helpers';

/**
 * This function converts the legacy VEP Pre-Event data structure
 * to the new Splash CMS Engine data structure
 *
 * The relevant elements that will be transformed are:
 * - body
 * - header
 * - modal
 * - modalLogo
 * - modalHeadline
 * - modalBody * UNIQUE TO THIS STATE
 * - footer
 * - contact
 * - eventPage
 * - footerCTA
 *
 * @param {object} newDataStructure - an accumulator object that will be used to store the transformed data
 * @param {string} elementId - the element ID of the element being transformed
 * @param {object} elementConfig - the element configuration of the element being transformed
 */
export const transformPreEventStateData = (newDataStructure, [elementId, elementConfig]) => {
    const { styles, properties, content, overrides = {} } = elementConfig;

    const defaultProperties = Array.isArray(properties) ? {} : properties;
    const defaultStyles = Array.isArray(styles) ? {} : styles;
    const overrideProperties = Array.isArray(overrides?.properties) ? {} : overrides?.properties;
    const overrideStyles = Array.isArray(overrides?.styles) ? {} : overrides?.styles;

    const mergedProperties = mergeWith({}, defaultProperties, overrideProperties || {}, (a, b) => (isNil(b) ? a : b));
    const mergedStyles = mergeWith({}, defaultStyles, overrideStyles || {}, (a, b) => (isNil(b) ? a : b));

    const { logoType, label, buttonLabel, image, ...restProperties } = mergedProperties;
    const metaContent = label || buttonLabel || content;
    const dynamicTagRegex = new RegExp(/^\[.*\]$/);

    const newStyles = Object.entries(mergedStyles).reduce((acc, [key, value]) => {
        const engineKey = stylesMapKeys.includes(key) ? allStylesMap[key] : key;

        if (!isNil(value)) {
            acc[engineKey] = value;
        }

        return acc;
    }, {});

    const newMeta = Object.entries(restProperties).reduce((acc, [key, value]) => {
        const engineKey = metaMapKeys.includes(key) ? metaMap[key] : key;

        if (!isNil(value)) {
            acc[engineKey] = value;
        }

        return acc;
    }, {});

    if (logoType) {
        newMeta.logoType = logoMap[logoType];
    } else if (elementId === ELEMENT_MODAL_LOGO) {
        newMeta.logoType = logoMap.customLogo;
    }

    if (elementId === ELEMENT_FOOTER_CTA) {
        newStyles.borderWidth = { ...EMPTY_BORDER_WIDTH };
    }

    if (image) {
        newStyles.backgroundImageUrl = image;
    }

    if (metaContent && !dynamicTagRegex.test(metaContent)) {
        newMeta.content = metaContent;
    }

    // Removes layoutType from the header of the pre-event because doesn't make sense
    delete newMeta.layoutType;

    const camelizedElementID = camelCase(elementId);

    // this object MUST be in the { props } format to match the saved data structure
    // eslint-disable-next-line no-param-reassign
    newDataStructure[camelizedElementID] = {
        props: {
            styles: newStyles,
            meta: newMeta,
        },
    };

    checkForUnknownKeys(mergedProperties, mergedStyles, elementId);

    return newDataStructure;
};
